import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { CityAutocomplete } from "../../components/CityAutocomplete.js";
import type { City } from "../../lib/api/cities.js";
import {
  type EntryType,
  type NewTravel,
  type TravelEntry,
  travelApi,
} from "../../lib/api/travel.js";
import { flag } from "../../lib/flag.js";
import "./Trips.css";

const TYPES: { value: EntryType; label: string; emoji: string }[] = [
  { value: "trip", label: "Trip", emoji: "✈️" },
  { value: "vacation", label: "Vacation", emoji: "🏖️" },
  { value: "nomadic", label: "Nomadic", emoji: "🌍" },
  { value: "timeoff", label: "Time off", emoji: "☕" },
  { value: "medical", label: "Medical", emoji: "➕" },
];

interface Draft {
  city: City | null;
  date_from: string;
  date_to: string;
  entry_type: EntryType;
}
const EMPTY: Draft = { city: null, date_from: "", date_to: "", entry_type: "trip" };

function fmtDate(d: string): string {
  return new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function fmtRange(from: string, to: string | null): string {
  return to ? `${fmtDate(from)} – ${fmtDate(to)}` : `from ${fmtDate(from)}`;
}
function typeMeta(t: EntryType): { value: EntryType; label: string; emoji: string } {
  return TYPES.find((x) => x.value === t) ?? { value: t, label: t, emoji: "✈️" };
}

function TripList({
  title,
  items,
  onRemove,
  muted,
}: {
  title: string;
  items: TravelEntry[];
  onRemove: (id: string) => void;
  muted?: boolean;
}) {
  return (
    <div className={`trips__group${muted ? " is-muted" : ""}`}>
      <h2 className="trips__h2">{title}</h2>
      <ol className="trips__list">
        {items.map((t) => {
          const meta = typeMeta(t.entry_type);
          return (
            <li key={t.id} className="trips__item">
              <span className="trips__flag" aria-hidden="true">
                {flag(t.country_code)}
              </span>
              <span className="trips__where">
                <b>{t.city}</b>
                <span className="trips__country">{t.country}</span>
              </span>
              <span className="trips__when">
                <span className="trips__dates">{fmtRange(t.date_from, t.date_to)}</span>
                <span className="trips__type-tag">
                  {meta.emoji} {meta.label}
                </span>
              </span>
              <button
                type="button"
                className="trips__del"
                onClick={() => onRemove(t.id)}
                aria-label={`delete trip to ${t.city}`}
              >
                ×
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function Trips() {
  const [trips, setTrips] = useState<TravelEntry[] | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const load = useCallback(() => {
    travelApi.list().then((r) => setTrips(r.success && r.data ? r.data.travel : []));
  }, []);
  useEffect(() => load(), [load]);

  const dateError =
    draft.date_from && draft.date_to && draft.date_to < draft.date_from
      ? "End date is before the start date."
      : null;
  const canSubmit = Boolean(draft.city && draft.date_from && !dateError);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!draft.city || !draft.date_from || dateError) return;
    setBusy(true);
    setMsg(null);
    const payload: NewTravel = {
      city: draft.city.name,
      country: draft.city.country,
      country_code: draft.city.country_code,
      timezone: draft.city.timezone,
      latitude: draft.city.latitude,
      longitude: draft.city.longitude,
      date_from: draft.date_from,
      date_to: draft.date_to || null,
      entry_type: draft.entry_type,
    };
    const res = await travelApi.create(payload);
    setBusy(false);
    if (res.success) {
      setMsg({
        kind: "ok",
        text: `Added ${draft.city.name} · ${fmtRange(draft.date_from, draft.date_to || null)}`,
      });
      setDraft(EMPTY);
      load();
    } else {
      setMsg({ kind: "err", text: res.error ?? "Couldn't add that trip — please try again." });
    }
  }

  async function remove(id: string) {
    const snapshot = trips;
    setTrips((cur) => cur?.filter((t) => t.id !== id) ?? cur); // optimistic
    const res = await travelApi.remove(id);
    if (!res.success) setTrips(snapshot ?? null); // revert
  }

  const today = new Date().toISOString().slice(0, 10);
  const { upcoming, past } = useMemo(() => {
    const list = trips ?? [];
    const up = list
      .filter((t) => (t.date_to ?? t.date_from) >= today)
      .sort((a, b) => a.date_from.localeCompare(b.date_from));
    const pa = list
      .filter((t) => (t.date_to ?? t.date_from) < today)
      .sort((a, b) => b.date_from.localeCompare(a.date_from));
    return { upcoming: up, past: pa };
  }, [trips, today]);

  return (
    <section className="trips">
      <header className="trips__head">
        <h1 className="trips__h1">Trips</h1>
        <p className="trips__sub">Where you're headed. Your people see where your plans overlap.</p>
      </header>

      <form className="trips__add" onSubmit={add}>
        <h2 className="trips__h2">Add a trip</h2>

        <div className="trips__field">
          <span className="trips__flabel">Where to?</span>
          {draft.city ? (
            <div className="trips__chip">
              <span aria-hidden="true">{flag(draft.city.country_code)}</span>
              <span>
                <b>{draft.city.name}</b>, {draft.city.country}
              </span>
              <button
                type="button"
                className="trips__chip-x"
                aria-label="change destination"
                onClick={() => setDraft((d) => ({ ...d, city: null }))}
              >
                Change
              </button>
            </div>
          ) : (
            <CityAutocomplete
              label="trip destination"
              onSelect={(city) => setDraft((d) => ({ ...d, city }))}
            />
          )}
        </div>

        <div className="trips__row">
          <label className="trips__field">
            <span className="trips__flabel">From</span>
            <input
              type="date"
              aria-label="from date"
              className="trips__date"
              value={draft.date_from}
              onChange={(e) => setDraft((d) => ({ ...d, date_from: e.target.value }))}
            />
          </label>
          <label className="trips__field">
            <span className="trips__flabel">
              To <span className="trips__opt">· optional</span>
            </span>
            <input
              type="date"
              aria-label="to date"
              className="trips__date"
              value={draft.date_to}
              min={draft.date_from || undefined}
              onChange={(e) => setDraft((d) => ({ ...d, date_to: e.target.value }))}
            />
          </label>
        </div>
        {dateError && (
          <p className="trips__err" role="alert">
            {dateError}
          </p>
        )}

        <fieldset className="trips__types">
          <legend className="trips__flabel">Type</legend>
          {TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              className={`trips__type${draft.entry_type === t.value ? " is-on" : ""}`}
              aria-pressed={draft.entry_type === t.value}
              onClick={() => setDraft((d) => ({ ...d, entry_type: t.value }))}
            >
              <span aria-hidden="true">{t.emoji}</span> {t.label}
            </button>
          ))}
        </fieldset>

        <button
          type="submit"
          className="btn btn-primary trips__submit"
          disabled={!canSubmit || busy}
        >
          {busy ? "Adding…" : "Add trip"}
        </button>
        {msg ? (
          <output className={`trips__msg trips__msg--${msg.kind}`}>
            {msg.kind === "ok" ? "✓ " : ""}
            {msg.text}
          </output>
        ) : (
          !draft.city && <p className="trips__hint">Search for your destination to get started.</p>
        )}
      </form>

      {trips === null ? (
        <p className="trips__loading">Loading your trips…</p>
      ) : trips.length === 0 ? (
        <p className="trips__empty">No trips yet — add where you're headed above.</p>
      ) : (
        <>
          {upcoming.length > 0 && <TripList title="Upcoming" items={upcoming} onRemove={remove} />}
          {past.length > 0 && <TripList title="Past" items={past} onRemove={remove} muted />}
        </>
      )}
    </section>
  );
}
